import type Store from "@ember-data/store";
import { tracked } from "@glimmer/tracking";
import type { DmpAdapter } from "parog-web/cnam-adapters/dmp-adapter";
import type { InsiAdapter } from "parog-web/cnam-adapters/insi-adapter";
import type { INS } from "parog-web/cnam/types";
import { InsStatus } from "parog-web/cnam/types";
import RetrieveAndSaveDocuments from "parog-web/components/cnam-services-access-form/process/loading/retrieve-and-save-documents";
import ProcessSuccessSaveOperations from "parog-web/components/cnam-services-access-form/process/success/save-operations/index";
import DmpDocumentMetadata from "parog-web/models/dmp-document-metadata";
import type DmpDocumentRetrievedJournalEvent from "parog-web/models/dmp-document-retrieved-journal-event";
import File from "parog-web/models/file";
import type Patient from "parog-web/models/patient";
import type ReimbursementsRetrievedJournalEvent from "parog-web/models/reimbursements-retrieved-journal-event";
import type FetcherService from "parog-web/services/fetcher";
import { b64DecodeUnicode } from "parog-web/utils/base64-decode-unicode";
import { CnamApiDmpDocumentFailedToSave } from "parog-web/utils/cnam-api-errors";
import createReimbursementFromXML from "parog-web/utils/dmp-parsing";
import { sortBy } from "parog-web/utils/sort";
import ControlTower from "wise-control-tower";
import { State } from "./abstract/cnam-context";
import DmpContext from "./abstract/dmp-context";
import type { AuthenticationContext } from "./authentication-context";

interface DmpDocumentRetrievedJournalEventWithMetadata {
  documentMetadata: DmpDocumentMetadata;
  dmpDocumentRetrievedJournalEvent: DmpDocumentRetrievedJournalEvent;
}

export default class DmpRetrievalContext extends DmpContext {
  fetcher: FetcherService;

  @tracked documentsMetadataToRetrieve: DmpDocumentMetadata[] = [];
  @tracked
  documentsRetrievedJournalEventsToUpdate: DmpDocumentRetrievedJournalEventWithMetadata[] =
    [];
  @tracked rembDocumentsMetadataToRetrieve: DmpDocumentMetadata[] = [];
  @tracked hrRetrieved = false;
  @tracked hrProcessEnded = false;
  @tracked nonHrProcessEnded = false;
  @tracked nonHrRetrieved = false;
  @tracked nonHrUpdated = false;
  @tracked documentsMetadataSaved = false;
  @tracked declare documentRetrievingIndex?: number;
  @tracked declare totalDocumentsToRetrieve?: number;
  @tracked declare documentUpdatingIndex?: number;
  @tracked declare totalDocumentsToUpdate?: number;
  @tracked nonHrDocumentsOnError: DmpDocumentMetadata[] = [];
  @tracked nonHrDocumentsUpdatesOnError: DmpDocumentMetadata[] = [];
  @tracked documentsFound = false;

  successComponent = ProcessSuccessSaveOperations;

  constructor(
    cnamAdapterName: string,
    patient: Patient,
    authenticationContext: AuthenticationContext,
    dmpAdapter: DmpAdapter,
    insiAdapter: InsiAdapter,
    store: Store,
    fetcher: FetcherService,
    requestManager,
    onSuccessfulHrRetrieval?: (
      reimbursementsRetrievedJournalEvent: ReimbursementsRetrievedJournalEvent
    ) => void
  ) {
    super(
      cnamAdapterName,
      patient,
      authenticationContext,
      dmpAdapter,
      insiAdapter,
      store,
      requestManager,
      () => {
        this.store.createRecord("reimbursements-retrieved-journal-event", {
          patient,
          occurredAt: new Date(),
          success: false,
          cnamProcessStartedJournalEvent: this.cnamProcessStartedJournalEvent
        });
      }
    );

    this.fetcher = fetcher;

    this.states = [
      ...this.states,
      new State(
        "find-documents",
        (): Promise<boolean> => {
          return Promise.resolve(
            !this.documentsFound &&
              this.patient.insStatus == InsStatus.Qualified
          );
        },
        async (): Promise<void> => {
          const documents = await this.findDocuments(this.patient, this.store);
          const jes = (
            await fetcher.query("dmp-document-retrieved-journal-event", {
              filter: { patient_id: patient.id }
            })
          ).slice();
          this.rembDocumentsMetadataToRetrieve =
            await this.getRembDocumentsMetadataToRetrieve(documents, jes);
          this.documentsMetadataToRetrieve =
            await this.getDocumentsMetadataToRetrieve(documents, jes);
          this.documentsRetrievedJournalEventsToUpdate =
            await this.getDocumentsRetrievedJournalEventsToUpdate(
              documents,
              jes
            );
          this.documentsFound = true;
        }
      ),
      new State(
        "save-documents-metadata",
        (): Promise<boolean> => {
          return Promise.resolve(!this.documentsMetadataSaved);
        },
        async (): Promise<void> => {
          await this.store
            .createRecord("dmp-documents-metadata-retrieved-journal-event", {
              occurredAt: new Date(),
              patient: this.patient,
              documentsMetadata: this.documentsMetadataToRetrieve,
              cnamProcessStartedJournalEvent:
                this.cnamProcessStartedJournalEvent
            })
            .save();

          this.documentsMetadataSaved = true;
        }
      ),
      new State(
        "retrieve-and-save-latest-reimbursements-document",
        (): Promise<boolean> => {
          return Promise.resolve(
            !this.hrProcessEnded &&
              this.patient.insStatus == InsStatus.Qualified
          );
        },
        async (): Promise<void> => {
          if (this.rembDocumentsMetadataToRetrieve.length > 0) {
            await this.retrieveAndSaveLastReimbursementDocument(
              this.rembDocumentsMetadataToRetrieve as DmpDocumentMetadata[],
              this.patient,
              this.dmpAdapter,
              this.secretConnection!,
              this.store,
              () => {
                this.hrRetrieved = true;
                return onSuccessfulHrRetrieval;
              }
            );
          }

          this.hrProcessEnded = true;
        }
      ),
      new State(
        "retrieve-and-save-documents",
        (): Promise<boolean> => {
          return Promise.resolve(
            !this.nonHrProcessEnded &&
              this.patient.insStatus == InsStatus.Qualified
          );
        },
        async (): Promise<void> => {
          if (this.documentsMetadataToRetrieve.length > 0) {
            await this.retrieveAndSaveNonReimbursementDocuments(
              this.documentsMetadataToRetrieve as DmpDocumentMetadata[],
              this.patient,
              this.dmpAdapter,
              this.secretConnection!,
              this.store,
              () => {
                this.nonHrRetrieved = true;
              }
            );
          }

          if (this.documentsRetrievedJournalEventsToUpdate.length > 0) {
            await this.retrieveAndUpdateNonReimbursementDocuments(
              this
                .documentsRetrievedJournalEventsToUpdate as DmpDocumentRetrievedJournalEventWithMetadata[],
              this.patient,
              this.dmpAdapter,
              this.secretConnection!,
              () => {
                this.nonHrUpdated = true;
              }
            );
          }

          this.nonHrProcessEnded = true;
        },
        undefined,
        RetrieveAndSaveDocuments
      ),
      new State(
        "save-operations",
        (): Promise<boolean> => {
          return Promise.resolve(
            this.hrProcessEnded &&
              this.nonHrProcessEnded &&
              this.patient.insStatus == InsStatus.Qualified
          );
        },
        async (): Promise<void> => {
          await this.store
            .createRecord("cnam-process-ended-journal-event", {
              timedTransactions: this.timedTransactions,
              occurredAt: new Date(),
              patient: this.patient,
              cnamProcessStartedJournalEvent:
                this.cnamProcessStartedJournalEvent
            })
            .save();

          this.successKey = "dmp-retrieval";
        }
      )
    ];
  }

  reset() {
    super.reset();
    this.successKey = undefined;
    this.documentsMetadataToRetrieve = [];
    this.documentsRetrievedJournalEventsToUpdate = [];
    this.rembDocumentsMetadataToRetrieve = [];
    this.hrRetrieved = false;
    this.hrProcessEnded = false;
    this.nonHrProcessEnded = false;
    this.nonHrRetrieved = false;
    this.nonHrUpdated = false;
    this.documentsMetadataSaved = false;
    this.documentsFound = false;
    this.nonHrDocumentsOnError = [];
    this.nonHrDocumentsUpdatesOnError = [];
  }

  // Those are documents that can been retrieved but the cdaHeaders
  // or the classCode or type are undefined
  async getDocumentsRetrievedJournalEventsToUpdate(
    documents: DmpDocumentMetadata[],
    dmpDocumentRetrievedJournalEvents: DmpDocumentRetrievedJournalEvent[]
  ): Promise<DmpDocumentRetrievedJournalEventWithMetadata[]> {
    const documentsUniqueIdsInMetadata = documents.map(
      document => document.uniqueId
    );

    const isDmpDocumentRetrievedJournalEventMissingAnAttribute =
      dmpDocumentRetrievedJournalEvent => {
        return (
          !dmpDocumentRetrievedJournalEvent.cdaHeaders ||
          !dmpDocumentRetrievedJournalEvent.classCode ||
          !dmpDocumentRetrievedJournalEvent.docType
        );
      };
    return dmpDocumentRetrievedJournalEvents
      .slice()
      .filter(
        dmpDocumentRetrievedJournalEvent =>
          isDmpDocumentRetrievedJournalEventMissingAnAttribute(
            dmpDocumentRetrievedJournalEvent
          ) &&
          documentsUniqueIdsInMetadata.includes(
            dmpDocumentRetrievedJournalEvent.uniqueId
          ) &&
          dmpDocumentRetrievedJournalEvent.docType !== "87273-9" &&
          dmpDocumentRetrievedJournalEvent.docType !== "REMB" &&
          dmpDocumentRetrievedJournalEvent.classCode !== "60"
      )
      .map(dmpDocumentRetrievedJournalEvent => {
        const documentMetadata = documents.find(
          document =>
            document.uniqueId === dmpDocumentRetrievedJournalEvent.uniqueId
        ) as DmpDocumentMetadata;
        return {
          documentMetadata,
          dmpDocumentRetrievedJournalEvent
        };
      });
  }

  async getRembDocumentsMetadataToRetrieve(
    documents: DmpDocumentMetadata[],
    dmpDocumentRetrievedJournalEvents: DmpDocumentRetrievedJournalEvent[]
  ): Promise<DmpDocumentMetadata[]> {
    return documents.filter(
      document =>
        document.type === "REMB" &&
        document.classCode === "60" &&
        !dmpDocumentRetrievedJournalEvents.find(
          event => event.uniqueId === document.uniqueId
        )
    );
  }
  async getDocumentsMetadataToRetrieve(
    documents: DmpDocumentMetadata[],
    dmpDocumentRetrievedJournalEvents: DmpDocumentRetrievedJournalEvent[]
  ): Promise<DmpDocumentMetadata[]> {
    const alreadyRetrievedDocumentsIds = dmpDocumentRetrievedJournalEvents
      .slice()
      .map(
        dmpDocumentRetrievedJournalEvent =>
          dmpDocumentRetrievedJournalEvent.uniqueId
      );

    const documentsToReturn =
      alreadyRetrievedDocumentsIds.length > 0
        ? documents.filter(
            document =>
              !alreadyRetrievedDocumentsIds.includes(document.uniqueId) &&
              document.type !== "87273-9" &&
              document.type !== "REMB" &&
              document.type !== "60"
          )
        : documents;
    return documentsToReturn.map(document => new DmpDocumentMetadata(document));
  }

  private async retrieveAndUpdateNonReimbursementDocuments(
    documentsRetrievedJournalEventsToUpdate: DmpDocumentRetrievedJournalEventWithMetadata[],
    patient: Patient,
    dmpAdapter: DmpAdapter,
    secretConnection: boolean,
    onSuccess: () => void
  ): Promise<void> {
    if (documentsRetrievedJournalEventsToUpdate.length > 0) {
      this.totalDocumentsToUpdate =
        documentsRetrievedJournalEventsToUpdate.length;
      const uniqueIds: Array<string> = [];

      for (const [
        index,
        documentRetrievedJournalEventToUpdate
      ] of documentsRetrievedJournalEventsToUpdate.entries()) {
        this.documentUpdatingIndex = index;
        // In order to not stop the process, we
        // catch the error
        try {
          const documentContent = await dmpAdapter.td32RetrieveContent(
            patient.ins as INS,
            documentRetrievedJournalEventToUpdate.documentMetadata,
            secretConnection
          );
          if (documentContent.cdaHeaders) {
            documentRetrievedJournalEventToUpdate.dmpDocumentRetrievedJournalEvent.cdaHeaders =
              documentContent.cdaHeaders;
          }

          documentRetrievedJournalEventToUpdate.dmpDocumentRetrievedJournalEvent.classCode ??=
            documentRetrievedJournalEventToUpdate.documentMetadata.classCode;
          documentRetrievedJournalEventToUpdate.dmpDocumentRetrievedJournalEvent.docType ??=
            documentRetrievedJournalEventToUpdate.documentMetadata.type;
          await documentRetrievedJournalEventToUpdate.dmpDocumentRetrievedJournalEvent.save();

          uniqueIds.push(
            documentRetrievedJournalEventToUpdate.documentMetadata.uniqueId
          );
        } catch (error) {
          this.nonHrDocumentsUpdatesOnError.push(
            documentRetrievedJournalEventToUpdate.documentMetadata
          );
          console.error("Details", error.error);
          ControlTower.captureException(error);
        }
      }
      if (uniqueIds.length > 0) {
        onSuccess();
      }
    }
  }

  private async retrieveAndSaveNonReimbursementDocuments(
    nonReimbursementsDocumentsMetadata: DmpDocumentMetadata[],
    patient: Patient,
    dmpAdapter: DmpAdapter,
    secretConnection: boolean,
    store: Store,
    onSuccess: () => void
  ): Promise<void> {
    if (nonReimbursementsDocumentsMetadata.length > 0) {
      this.totalDocumentsToRetrieve = nonReimbursementsDocumentsMetadata.length;
      const uniqueIds: Array<string> = [];

      for (const [
        index,
        documentMetadata
      ] of nonReimbursementsDocumentsMetadata.entries()) {
        this.documentRetrievingIndex = index;

        // In order to not stop the process, we
        // catch the error
        try {
          const documentContent = await dmpAdapter.td32RetrieveContent(
            patient.ins as INS,
            documentMetadata,
            secretConnection
          );

          const fileAttachment = store.createRecord("file-attachment", {
            mimeType: documentMetadata.mimeType,
            index: 0
          });

          const fileContent = store.createRecord("file-content", {
            data: documentContent.body,
            fileAttachment
          });

          const file = new File(fileAttachment, fileContent);

          const journalEventPayload = {
            type: "dmp-document-retrieved-journal-events",
            relationships: {
              patient: {
                data: {
                  id: patient.id,
                  type: "patients"
                }
              },
              "cnam-process-started-journal-event": {
                data: {
                  id: this.cnamProcessStartedJournalEvent.id,
                  type: "cnam-process-started-journal-events"
                }
              }
            },
            attributes: {
              occurredAt: documentMetadata.creationTime,
              title: documentMetadata.title,
              classCode: documentMetadata.classCode,
              docType: documentMetadata.type,
              mimeType: documentMetadata.mimeType,
              uniqueId: documentMetadata.uniqueId,
              cdaHeaders: documentContent.cdaHeaders
            }
          };

          const body = JSON.stringify({
            data: {
              "journal-event": {
                data: journalEventPayload
              },
              "file-attachments": [file.fileAttachment.serialize()],
              "file-contents": [file.fileContent.serialize()]
            }
          });

          const { response, content } = await this.requestManager.request({
            url: "/api/v1/journal_event_with_attachment",
            method: "POST",
            body: body
          });

          if (!response.ok) {
            throw new CnamApiDmpDocumentFailedToSave(
              `Failed to save document with uniqueId ${documentMetadata.uniqueId}`
            );
          } else {
            // Fetch saved record
            await this.store.findRecord(
              content.data.type.slice(0, -1),
              content.data.id,
              {
                include: "file-attachments"
              }
            );
          }

          uniqueIds.push(documentMetadata.uniqueId);
        } catch (error) {
          this.nonHrDocumentsOnError.push(documentMetadata);
          console.error("Details", error.error);
          ControlTower.captureException(error);
        }
      }

      if (uniqueIds.length > 0) {
        onSuccess();
      }
    }
  }

  private async retrieveAndSaveLastReimbursementDocument(
    reimbursementsDocumentsMetadata: DmpDocumentMetadata[],
    patient: Patient,
    dmpAdapter: DmpAdapter,
    secretConnection: boolean,
    store: Store,
    onSuccessfulHrRetrieval?: (
      reimbursementsRetrievedJournalEvent: ReimbursementsRetrievedJournalEvent
    ) => void
  ): Promise<void> {
    if (reimbursementsDocumentsMetadata.length > 0) {
      const reimbursementsDocumentContent =
        await dmpAdapter.td32RetrieveContent(
          patient.ins as INS,
          reimbursementsDocumentsMetadata
            .sort(sortBy("creationTime"))
            .at(-1) as DmpDocumentMetadata,
          secretConnection
        );

      const xml = b64DecodeUnicode(
        reimbursementsDocumentContent.cdaContent as string
      );
      const je = await createReimbursementFromXML(
        xml,
        store,
        patient,
        this.cnamProcessStartedJournalEvent
      );
      onSuccessfulHrRetrieval?.(je);
    }
  }
}
